import React, { createContext, useContext } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { DeanteData } from "./AppProvider";
import { usePersistedState } from "./hooks/usePersistedState";
import { useAppContext } from "./AppContext";
import { useAPI } from "../API";
import { useUserState } from "./hooks/useUserState";
import CartFloatingBar from "../../../Cart/CartFloatingBar";
import CartHistoryFloatingHost from "../../../Cart/CartHistoryFloatingHost";
import { parseNumericValue } from "../../../Cart/helpers";
import type { OrderHistorySummary } from "../../../Cart/cartTypes";

const CART_STORAGE_KEY = "currentOrderCart";

export type CartState = Record<string, number>;

export interface ResolvedCartItem {
  id: string;
  quantity: number;
  unit: DeanteData;
}

interface CartContextValue {
  itemsById: CartState;
  hydrated: boolean;
  itemIds: string[];
  uniqueItemsCount: number;
  totalQuantity: number;
  floatingCartBottomInset: number;
  subtotal: number;
  discountedSubtotal: number;
  resolvedItems: ResolvedCartItem[];
  missingItemIds: string[];
  historyOrders: OrderHistorySummary[];
  historyOrdersLoading: boolean;
  refreshHistoryOrders: () => Promise<void>;
  addItem: (id: string | number, quantity?: number) => void;
  incrementItem: (id: string | number) => void;
  decrementItem: (id: string | number) => void;
  setQuantity: (id: string | number, quantity: number) => void;
  removeItem: (id: string | number) => void;
  clearCart: () => void;
  getQuantity: (id: string | number) => number;
  isInCart: (id: string | number) => boolean;
}

const CartContext = createContext<CartContextValue | null>(null);

const normalizeId = (id: string | number) => String(id);

const normalizeQuantity = (quantity: number) => {
  if (!Number.isFinite(quantity)) return 0;
  return Math.max(0, Math.floor(quantity));
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { allUnitData, USER, isSmallVersion, scaleAll } = useAppContext();
  const insets = useSafeAreaInsets();
  const { sendFormDataPostRequest } = useAPI();
  const userState = useUserState();
  const [storedItemsById, setStoredItemsById, hydrated] = usePersistedState<CartState>({
    key: CART_STORAGE_KEY,
    initial: {},
  });
  const [historyOrders, setHistoryOrders] = React.useState<OrderHistorySummary[]>([]);
  const [historyOrdersLoading, setHistoryOrdersLoading] = React.useState(false);
  const historyOrdersRequestRef = React.useRef<Promise<void> | null>(null);

  const itemsById = storedItemsById ?? {};

  const itemIds = React.useMemo(() => Object.keys(itemsById), [itemsById]);

  const uniqueItemsCount = itemIds.length;

  const totalQuantity = React.useMemo(
    () =>
      itemIds.reduce((sum, id) => {
        return sum + normalizeQuantity(itemsById[id] ?? 0);
      }, 0),
    [itemIds, itemsById]
  );

  const floatingCartBottomInset = React.useMemo(() => {
    const defaultInset = 48 * scaleAll;
    if (totalQuantity <= 0) return defaultInset;

    const barHeight = (isSmallVersion ? 68 : 76) * scaleAll;
    const bottomInset = Math.max(insets.bottom + 10 * scaleAll, 16 * scaleAll);

    return Math.max(defaultInset, barHeight + bottomInset + 16 * scaleAll);
  }, [insets.bottom, isSmallVersion, scaleAll, totalQuantity]);

  const resolvedItems = React.useMemo<ResolvedCartItem[]>(() => {
    if (!allUnitData) return [];

    return itemIds
      .map((id) => {
        const unit = allUnitData[id];
        const quantity = normalizeQuantity(itemsById[id] ?? 0);

        if (!unit || quantity <= 0) return null;

        return {
          id,
          quantity,
          unit,
        };
      })
      .filter(Boolean) as ResolvedCartItem[];
  }, [allUnitData, itemIds, itemsById]);

  const missingItemIds = React.useMemo(() => {
    if (!allUnitData) return itemIds;
    return itemIds.filter((id) => !allUnitData[id]);
  }, [allUnitData, itemIds]);

  const subtotal = React.useMemo(
    () =>
      resolvedItems.reduce((sum, item) => {
        return sum + parseNumericValue(item.unit.price) * item.quantity;
      }, 0),
    [resolvedItems]
  );

  const discountedSubtotal = React.useMemo(
    () =>
      resolvedItems.reduce((sum, item) => {
        return sum + parseNumericValue(item.unit.discounted_price) * item.quantity;
      }, 0),
    [resolvedItems]
  );

  const setQuantity = React.useCallback(
    (id: string | number, quantity: number) => {
      const normalizedId = normalizeId(id);
      const normalizedQuantity = normalizeQuantity(quantity);

      setStoredItemsById((prev) => {
        const next = { ...(prev ?? {}) };

        if (normalizedQuantity <= 0) {
          delete next[normalizedId];
        } else {
          next[normalizedId] = normalizedQuantity;
        }

        return Object.keys(next).length ? next : null;
      });
    },
    [setStoredItemsById]
  );

  const addItem = React.useCallback(
    (id: string | number, quantity = 1) => {
      const normalizedId = normalizeId(id);
      const normalizedQuantity = normalizeQuantity(quantity);
      if (normalizedQuantity <= 0) return;

      setStoredItemsById((prev) => {
        const next = { ...(prev ?? {}) };
        next[normalizedId] = normalizeQuantity((next[normalizedId] ?? 0) + normalizedQuantity);
        return next;
      });
    },
    [setStoredItemsById]
  );

  const incrementItem = React.useCallback(
    (id: string | number) => {
      addItem(id, 1);
    },
    [addItem]
  );

  const decrementItem = React.useCallback(
    (id: string | number) => {
      const normalizedId = normalizeId(id);

      setStoredItemsById((prev) => {
        const next = { ...(prev ?? {}) };
        const nextQuantity = normalizeQuantity((next[normalizedId] ?? 0) - 1);

        if (nextQuantity <= 0) {
          delete next[normalizedId];
        } else {
          next[normalizedId] = nextQuantity;
        }

        return Object.keys(next).length ? next : null;
      });
    },
    [setStoredItemsById]
  );

  const removeItem = React.useCallback(
    (id: string | number) => {
      const normalizedId = normalizeId(id);

      setStoredItemsById((prev) => {
        if (!prev) return prev;

        const next = { ...prev };
        delete next[normalizedId];

        return Object.keys(next).length ? next : null;
      });
    },
    [setStoredItemsById]
  );

  const clearCart = React.useCallback(() => {
    setStoredItemsById(null);
  }, [setStoredItemsById]);

  const getQuantity = React.useCallback(
    (id: string | number) => {
      return normalizeQuantity(itemsById[normalizeId(id)] ?? 0);
    },
    [itemsById]
  );

  const isInCart = React.useCallback(
    (id: string | number) => getQuantity(id) > 0,
    [getQuantity]
  );

  const refreshHistoryOrders = React.useCallback(async () => {
    if (!userState.isUserLoggedIn) {
      setHistoryOrders([]);
      return;
    }

    if (historyOrdersRequestRef.current) {
      await historyOrdersRequestRef.current;
      return;
    }

    const formData = new FormData();
    if (USER?.access_token) {
      formData.append("access_token", USER.access_token);
    }

    const request = (async () => {
      try {
        setHistoryOrdersLoading(true);

        const response = await sendFormDataPostRequest({
          link: "list-orders",
          formData,
        });

        if (response?.success && Array.isArray(response.orders)) {
          setHistoryOrders(response.orders);
          return;
        }

        setHistoryOrders([]);
      } catch {
        setHistoryOrders([]);
      } finally {
        setHistoryOrdersLoading(false);
        historyOrdersRequestRef.current = null;
      }
    })();

    historyOrdersRequestRef.current = request;
    await request;
  }, [USER?.access_token, sendFormDataPostRequest, userState.isUserLoggedIn]);

  React.useEffect(() => {
    void refreshHistoryOrders();
  }, [refreshHistoryOrders]);

  const value = React.useMemo<CartContextValue>(
    () => ({
      itemsById,
      hydrated,
      itemIds,
      uniqueItemsCount,
      totalQuantity,
      floatingCartBottomInset,
      subtotal,
      discountedSubtotal,
      resolvedItems,
      missingItemIds,
      historyOrders,
      historyOrdersLoading,
      refreshHistoryOrders,
      addItem,
      incrementItem,
      decrementItem,
      setQuantity,
      removeItem,
      clearCart,
      getQuantity,
      isInCart,
    }),
    [
      itemsById,
      hydrated,
      itemIds,
      uniqueItemsCount,
      totalQuantity,
      floatingCartBottomInset,
      subtotal,
      discountedSubtotal,
      resolvedItems,
      missingItemIds,
      historyOrders,
      historyOrdersLoading,
      refreshHistoryOrders,
      addItem,
      incrementItem,
      decrementItem,
      setQuantity,
      removeItem,
      clearCart,
      getQuantity,
      isInCart,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
      <CartFloatingBar
        subtotal={subtotal}
        discountedSubtotal={discountedSubtotal}
        totalQuantity={totalQuantity}
        uniqueItemsCount={uniqueItemsCount}
        resolvedItems={resolvedItems}
        missingItemIds={missingItemIds}
        incrementItem={incrementItem}
        decrementItem={decrementItem}
        removeItem={removeItem}
        clearCart={clearCart}
      />
      <CartHistoryFloatingHost />
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }

  return context;
};
